import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Users, MapPin, Search, Filter, 
  CheckCircle, Plus, Globe, Trophy 
} from 'lucide-react';
import { AZURE } from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import { useSportsLang } from '../contexts/SportsLangContext';

// === CONFIGURAÇÃO VISUAL (Design System ArenaStats) ===
const THEME = {
  primary: '#111111',    // Verde Brand
  gold: '#b48c2c',       // Dourado
  bg: '#f8fafc',         // Fundo slate-50 (Design System)
  white: '#ffffff',
  text: '#1f2937',
  textLight: '#6b7280'
};

const BORDER = '#E5E7EB';

const LABELS = {
  pt: {
    pageTitle: 'Organizações — Arena Stats',
    pageSubtitle: 'Clubes, federações e parceiros (ordenado por relevância)',
    pageBadge: 'ORGANIZAÇÕES',
    searchPlaceholder: 'Buscar organizações...',
    registerBtn: '+ Registrar Organização',
    filterAll: 'Todas',
    filterClubs: 'Clubes',
    filterFederations: 'Federações',
    filterSponsors: 'Patrocinadores',
    allSports: 'Todos os Esportes',
    swimming: 'Natação',
    football: 'Futebol',
    showingResults: (n) => `Mostrando ${n} resultados`,
    emptyTitle: 'Nenhuma base encontrada',
    emptyDesc: 'Tente buscar por outro termo ou limpar os filtros.',
    clearFilters: 'Limpar filtros',
    athletes: 'Atletas',
    followers: 'Seguidores',
    followBtn: 'Seguir',
    viewBtn: 'Ver',
  },
  en: {
    pageTitle: 'Organizations — Arena Stats',
    pageSubtitle: 'Clubs, federations and partners (sorted by relevance)',
    pageBadge: 'ORGANIZATIONS',
    searchPlaceholder: 'Search organizations...',
    registerBtn: '+ Register Organization',
    filterAll: 'All',
    filterClubs: 'Clubs',
    filterFederations: 'Federations',
    filterSponsors: 'Sponsors',
    allSports: 'All Sports',
    swimming: 'Swimming',
    football: 'Football',
    showingResults: (n) => `Showing ${n} results`,
    emptyTitle: 'No organizations found',
    emptyDesc: 'Try a different search term or clear the filters.',
    clearFilters: 'Clear filters',
    athletes: 'Athletes',
    followers: 'Followers',
    followBtn: 'Follow',
    viewBtn: 'View',
  },
};

// UI Primitives (padrão Design System ArenaStats)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

// === COMPONENTE: CARD DE BASE (SOCIAL) ===
const BaseCard = ({ org }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  // Lógica de Logo (Fallback inteligente)
  const logoSrc = org.logo_url || org.logo || '/logos-natacao/default-club.svg';
  const isVerified = org.is_verified || org.is_national_team;

  const sportsList = (() => {
    if (Array.isArray(org.sports) && org.sports.length > 0) return org.sports;
    if (typeof org.sport === 'string' && org.sport.trim()) return [org.sport.trim()];
    return [];
  })();
  
  // Define o link correto baseado no tipo e esporte
  const getOrgLink = () => {
    if (org.type === 'BRAND' || org.type === 'FEDERATION') {
      return `/base/${org.id}`;
    }

    const hasSwim = !!(org.sports && org.sports.includes('Natação')) || org.sport === 'Natação';
    const hasFoot = !!(org.sports && org.sports.includes('Futebol')) || org.sport === 'Futebol';
    const swimId = org.swim_id || org.original_id || org.id;
    const footId = org.foot_id || org.original_id || org.id;

    // Multiesporte: sempre enviar ambos ids no query string para unificar perfil no detalhe.
    if (hasSwim && hasFoot && org.swim_id && org.foot_id) {
      const qs = new URLSearchParams({
        swim_id: String(org.swim_id),
        foot_id: String(org.foot_id),
        prefer_logo: 'football',
        ...(Array.isArray(org.swim_ids) && org.swim_ids.length > 1
          ? { swim_ids: org.swim_ids.map(String).join(',') }
          : {})
      }).toString();
      return `/clube/natacao/${swimId}?${qs}`;
    }

    // Para clubes, usa o link específico do esporte
    if (hasSwim) {
      return `/clube/natacao/${swimId}`;
    }
    if (hasFoot) {
      return `/clube/futebol/${footId}`;
    }
    return `/base/${org.id}`;
  };

  const orgLink = getOrgLink();

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200 group flex flex-col h-full overflow-hidden" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
      
      {/* 1. Banner Verde (Brand) */}
      <div className="h-16 bg-gradient-to-r from-[#111111] to-[#0b0b0c] opacity-90 relative">
        {/* Banner verde com gradiente */}
      </div>

      {/* 2. Conteúdo Principal */}
      <div className="px-4 pb-4 flex-1 flex flex-col relative">
        
        {/* Logo (Avatar da Base) - Posicionado mais alto */}
        <div className="-mt-8 mb-3">
          <div className="w-16 h-16 bg-white rounded-xl border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
            <img 
              src={logoSrc} 
              alt={org.name || 'Organization'} 
              className="w-full h-full object-contain p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback de Iniciais */}
            <div className="hidden w-full h-full bg-gray-50 items-center justify-center text-xl font-bold text-gray-400">
              {org.name ? org.name.substring(0, 2).toUpperCase() : '??'}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-[#111111] transition-colors">
              <Link to={orgLink}>{org.name || 'Organização'}</Link>
            </h3>
            {isVerified && <CheckCircle size={16} className="text-[#111111] shrink-0" fill="#f3f4f6" />}
          </div>

          {/* Tags por esporte */}
          {sportsList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {sportsList.includes('Natação') && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                  {t.swimming}
                </span>
              )}
              {sportsList.includes('Futebol') && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-800 border border-sky-100">
                  {t.football}
                </span>
              )}
              {!sportsList.includes('Natação') && !sportsList.includes('Futebol') && (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-700 border border-gray-200">
                  {sportsList[0]}
                </span>
              )}
            </div>
          )}
          
          <p className="text-sm text-gray-500 mb-3 line-clamp-2">
            {org.description || `Base esportiva focada em ${org.sport || 'multiesporte'}.`}
          </p>

          {/* Stats Grid (Atletas + Seguidores) */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-[#111111]">{org.athletes_count || org.members_count || 0}</div>
              <div className="text-xs text-gray-500">{t.athletes}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-[#111111]">{org.followers_count || 120}</div>
              <div className="text-xs text-gray-500">{t.followers}</div>
            </div>
          </div>

          {/* Localização */}
          {org.city && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <MapPin size={12} /> {org.city}, {org.country}
            </div>
          )}
        </div>

        {/* 3. Rodapé de Ação */}
        <div className="mt-auto pt-4 border-t border-gray-100 flex gap-2">
          <button className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#111111] text-white text-sm font-bold hover:bg-[#0b0b0c] transition-colors">
            <Plus size={16} /> {t.followBtn}
          </button>
          <Link 
            to={orgLink}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t.viewBtn}
          </Link>
        </div>

      </div>
    </div>
  );
};

// === COMPONENTE PRINCIPAL: PAGE ===
const OrganizationsPage = () => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('ALL'); // ALL, CLUB, FEDERATION, BRAND
  const [selectedSport, setSelectedSport] = useState('ALL'); // ALL, FOOTBALL, SWIMMING
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carregar Dados do Backend
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('🔄 [OrganizationsPage] Iniciando busca de organizações...');
        
        // 1. Buscar clubes de Natação (carrega primeiro para não travar a tela)
        let swimClubs = [];
        try {
          const swimResponse = await AZURE.get('/swimming/clubs?limit=500');
          console.log('✅ [Natação] Resposta recebida:', swimResponse.data);
          // Backend retorna { success, items, count }
          const swimItems = swimResponse.data?.items || swimResponse.data?.clubs || swimResponse.data?.data?.items || swimResponse.data?.data || swimResponse.data || [];
          swimClubs = (Array.isArray(swimItems) ? swimItems : []).map(c => ({
            id: `swim-${c.id}`,
            original_id: c.id,
            swim_id: c.id,
            name: c.name,
            logo_url: c.logo_url || c.logo,
            type: c.is_national_team ? 'FEDERATION' : 'CLUB',
            sport: 'Natação',
            city: c.city || '',
            state: c.state || '',
            country: c.country || 'Brasil',
            is_verified: c.is_national_team || false,
            is_national_team: c.is_national_team || false,
            athletes_count: c.total_athletes || c.athletes_count || 0,
            medals_count: c.medals_count || 0,
            followers_count: (c.total_athletes || c.athletes_count) ? (c.total_athletes || c.athletes_count) * 10 : 100,
            description: c.is_national_team 
              ? `Seleção Nacional de Natação` 
              : `Clube de Natação com ${c.total_athletes || c.athletes_count || 'diversos'} atletas registrados`
          }));
          console.log(`✅ [Natação] ${swimClubs.length} clubes processados`);
        } catch (swimError) {
          console.error('❌ [Natação] Erro ao buscar clubes:', swimError);
        }

        // 2. Mock data de Patrocinadores (enquanto não temos tabela no banco)
        const sponsors = [
          {
            id: 'sponsor-1',
            name: 'Nike',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/a/a6/Logo_NIKE.svg',
            type: 'SPONSOR',
            sport: 'Multi-esporte',
            country: 'Estados Unidos',
            is_verified: true,
            followers_count: 150000,
            description: 'Marca global de equipamentos esportivos'
          },
          {
            id: 'sponsor-2',
            name: 'Adidas',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg',
            type: 'SPONSOR',
            sport: 'Multi-esporte',
            country: 'Alemanha',
            is_verified: true,
            followers_count: 140000,
            description: 'Líder mundial em equipamentos esportivos'
          },
          {
            id: 'sponsor-3',
            name: 'Speedo',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/9/9e/Speedo_logo.svg',
            type: 'SPONSOR',
            sport: 'Natação',
            country: 'Austrália',
            is_verified: true,
            followers_count: 45000,
            description: 'Marca especializada em natação'
          },
          {
            id: 'sponsor-4',
            name: 'Arena',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/4/4f/Arena_logo.svg',
            type: 'SPONSOR',
            sport: 'Natação',
            country: 'Itália',
            is_verified: true,
            followers_count: 38000,
            description: 'Equipamentos de alto desempenho para natação'
          },
          {
            id: 'sponsor-5',
            name: 'Penalty',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/7/7e/Penalty_logo.svg',
            type: 'SPONSOR',
            sport: 'Futebol',
            country: 'Brasil',
            is_verified: true,
            followers_count: 25000,
            description: 'Marca brasileira de artigos esportivos'
          },
          {
            id: 'sponsor-6',
            name: 'Umbro',
            logo_url: 'https://upload.wikimedia.org/wikipedia/commons/c/c0/Umbro_logo.svg',
            type: 'SPONSOR',
            sport: 'Futebol',
            country: 'Inglaterra',
            is_verified: true,
            followers_count: 32000,
            description: 'Marca tradicional de futebol'
          }
        ];

        // Normalização e merge
        // Objetivo: unificar perfis multi-esporte (ex: "Clube de Regatas do Flamengo" + "Flamengo")
        // e permitir que detalhes recebam swim_id e foot_id via query string.
        const normalizeClubName = (name = '') => {
          const raw = String(name || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

          // Aliases conhecidos (corrige casos em que um lado vem "completo" e o outro "curto")
          const alias = {
            'clubederegatasdoflamengo': 'flamengo',
            'crflamengo': 'flamengo',
            'clube de regatas do flamengo': 'flamengo',
            'fluminensefootballclub': 'fluminense',
            'fluminensefc': 'fluminense',
            'saopaulofutebolclube': 'saopaulo',
            'saopaulofc': 'saopaulo',
            'spfc': 'saopaulo'
          };

          // Remove caracteres não alfanuméricos e também tokens comuns que quebram o merge
          let compact = raw.replace(/[^a-z0-9]/g, '');
          if (alias[compact]) return alias[compact];

          compact = compact
            .replace(/futebolclube/g, '')
            .replace(/footballclub/g, '')
            .replace(/sportclub/g, '')
            .replace(/clube/g, '')
            .replace(/club/g, '')
            .replace(/futebol/g, '')
            .replace(/football/g, '')
            .replace(/associacao/g, '')
            .replace(/associacaoatletica/g, '')
            .replace(/esporteclube/g, '')
            .replace(/fc/g, '')
            .replace(/sc/g, '')
            .replace(/cr/g, '')
            .replace(/ec/g, '');

          // Remove sufixo de UF quando vem colado (ex: "fluminensefcrj", "saopaulosp")
          if (compact.length > 4) {
            compact = compact.replace(/(ac|al|ap|am|ba|ce|df|es|go|ma|mt|ms|mg|pa|pb|pr|pe|pi|rj|rn|rs|ro|rr|sc|sp|se|to)$/g, '');
          }

          // Segundo passe de alias após remoções
          if (alias[compact]) return alias[compact];
          return compact;
        };

        const mergedMap = new Map();
        const upsertClub = (club) => {
          const key = normalizeClubName(club.name);
          if (!key) return;

          const existing = mergedMap.get(key);
          if (!existing) {
            mergedMap.set(key, {
              ...club,
              sports: [club.sport],
              original_ids: [club.original_id],
              swim_id: club.swim_id || null,
              foot_id: club.foot_id || null,
              swim_ids: club.swim_id ? [club.swim_id] : [],
              foot_ids: club.foot_id ? [club.foot_id] : []
            });
            return;
          }

          const sports = new Set([...(existing.sports || []), club.sport]);
          // Preferir logo do futebol quando multisport (melhor reconhecimento de marca)
          const preferFootballLogo = club.sport === 'Futebol' && club.logo_url;

          const swimIds = new Set([...(existing.swim_ids || []), ...(club.swim_id ? [club.swim_id] : [])]);
          const footIds = new Set([...(existing.foot_ids || []), ...(club.foot_id ? [club.foot_id] : [])]);

          // Para Natação: escolher como swim_id "principal" o que tiver mais atletas
          const shouldReplaceSwimId =
            club.sport === 'Natação' &&
            club.swim_id &&
            (club.athletes_count || 0) > (existing.athletes_count || 0);

          mergedMap.set(key, {
            ...existing,
            name: existing.name || club.name,
            logo_url: preferFootballLogo ? club.logo_url : (existing.logo_url || club.logo_url),
            sports: Array.from(sports),
            sport: sports.size > 1 ? 'Multi-esporte' : Array.from(sports)[0],
            original_ids: [...(existing.original_ids || []), club.original_id],
            swim_id: shouldReplaceSwimId ? club.swim_id : (existing.swim_id || club.swim_id || null),
            foot_id: existing.foot_id || club.foot_id || null,
            swim_ids: Array.from(swimIds),
            foot_ids: Array.from(footIds),
            athletes_count: Math.max(existing.athletes_count || 0, club.athletes_count || 0),
            medals_count: (existing.medals_count || 0) + (club.medals_count || 0)
          });
        };

        swimClubs.forEach(upsertClub);

        // Render parcial (Natação + sponsors) imediatamente
        const initialClubs = Array.from(mergedMap.values());
        setOrganizations([...initialClubs, ...sponsors]);
        setLoading(false);

        // 3. Buscar clubes de Futebol em background (não bloqueia UI)
        let footClubs = [];
        try {
          const response = await AZURE.get('/football/clubs/directory', {
            params: { limit: 200, order: 'none' },
            timeout: 15000
          });
          console.log('✅ [Futebol] Resposta recebida:', response.data);

          const clubItems =
            response.data?.items ||
            response.data?.clubs ||
            response.data?.data?.items ||
            response.data?.data ||
            response.data ||
            [];
          footClubs = (Array.isArray(clubItems) ? clubItems : []).map(c => ({
            id: `foot-${c.club_id || c.id}`,
            original_id: c.club_id || c.id,
            foot_id: c.club_id || c.id,
            name: c.club_name || c.name,
            logo_url: c.logo_url || c.logo,
            type: 'CLUB',
            sport: 'Futebol',
            city: '',
            state: '',
            country: c.country || 'Brasil',
            is_verified: false,
            is_national_team: false,
            athletes_count: c.total_players || 0,
            medals_count: 0,
            followers_count: (c.total_matches || 0) * 5,
            description: `Clube de Futebol com ${c.total_matches || 0} partidas registradas e ${c.total_players || 0} jogadores`,
            primary_league_id: c.primary_league_id
          }));

          console.log(`✅ [Futebol] ${footClubs.length} clubes processados`);

          try {
            localStorage.setItem('arenastats_foot_clubs_cache_v1', JSON.stringify({
              ts: Date.now(),
              items: footClubs
            }));
          } catch (e) {
            // ignore
          }
        } catch (footError) {
          console.error('❌ [Futebol] Erro ao buscar clubes:', footError);
          try {
            const cached = JSON.parse(localStorage.getItem('arenastats_foot_clubs_cache_v1') || 'null');
            if (cached?.items && Array.isArray(cached.items) && cached.items.length > 0) {
              footClubs = cached.items;
              console.log(`🧠 [Futebol] Usando cache local: ${footClubs.length} clubes`);
            }
          } catch (e) {
            // ignore
          }
        }

        footClubs.forEach(upsertClub);

        const unifiedClubs = Array.from(mergedMap.values());
        const allOrgs = [...unifiedClubs, ...sponsors];
        console.log(`✅ [Total] ${allOrgs.length} organizações carregadas`);
        setOrganizations(allOrgs);

        return;
      } catch (error) {
        console.error("❌ [OrganizationsPage] Erro ao carregar organizações:", error);
      } finally {
        // loading é controlado após Natação + sponsors
      }
    };
    fetchData();
  }, []);

  // Filtragem Local
  const filteredOrgs = organizations.filter(org => {
    const matchesSearch = org.name && org.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'ALL' || org.type === selectedType;
    
    // Filtro de Esporte (Normalização básica)
    const sportsList = org.sports || [org.sport];
    const matchesSport = selectedSport === 'ALL' ||
      (selectedSport === 'FOOTBALL' && sportsList.includes('Futebol')) ||
      (selectedSport === 'SWIMMING' && sportsList.includes('Natação'));

    return matchesSearch && matchesType && matchesSport;
  });

  // 🔥 RISCO #2 FIX: Sort clubes por athletes_count (desc) quando filtro CLUB ativo
  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    // Ordenar clubes por número de atletas (desc)
    if (selectedType === 'CLUB' || selectedType === 'ALL') {
      const countA = a.athletes_count || a.members_count || 0;
      const countB = b.athletes_count || b.members_count || 0;
      return countB - countA; // Decrescente
    }
    return 0; // Sem ordenação para outros tipos
  });

  return (
    <PageShell>
      
      {/* 1. HEADER DA PÁGINA */}
      <div className="mb-6">
        <PageHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
          badgeText={t.pageBadge}
        />
      </div>

      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            
            {/* Barra de Busca e Ação */}
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <input 
                  type="text" 
                  placeholder={t.searchPlaceholder} 
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#111111] focus:bg-white transition-all text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              </div>
              <button className="px-4 py-2 bg-[#111111] text-white rounded-lg text-sm font-bold hover:bg-[#0b0b0c] transition-colors whitespace-nowrap">
                {t.registerBtn}
              </button>
            </div>

          </div>

          {/* Filtros Rápidos (Abas) */}
          <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2 scrollbar-hide">
            {[
              { id: 'ALL', label: t.filterAll },
              { id: 'CLUB', label: t.filterClubs, icon: Trophy },
              { id: 'FEDERATION', label: t.filterFederations, icon: Globe },
              { id: 'SPONSOR', label: t.filterSponsors, icon: Building2 },
            ].map(type => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all border whitespace-nowrap ${
                  selectedType === type.id 
                    ? 'bg-[#111111] text-white border-[#111111]' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {type.icon && <type.icon size={14} />}
                {type.label}
              </button>
            ))}
            
            {/* Divisor Vertical */}
            <div className="w-px h-6 bg-gray-300 mx-2"></div>

            {/* Filtro Esporte */}
            <select 
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 focus:outline-none"
            >
              <option value="ALL">{t.allSports}</option>
              <option value="SWIMMING">{t.swimming}</option>
              <option value="FOOTBALL">{t.football}</option>
            </select>
          </div>
        </div>
      </div>

      {/* 2. CONTEÚDO (GRID) */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {loading ? (
          // Skeleton Loading
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white h-72 rounded-xl border border-gray-200 animate-pulse"></div>
            ))}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              {t.showingResults(sortedOrgs.length)}
            </p>
            
            {sortedOrgs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {sortedOrgs.map((org, index) => (
                  <BaseCard key={`${org.id}-${index}`} org={org} />
                ))}
              </div>
            ) : (
              // Empty State
              <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900">{t.emptyTitle}</h3>
                <p className="text-gray-500">{t.emptyDesc}</p>
                <button 
                  onClick={() => { setSearchTerm(''); setSelectedType('ALL'); setSelectedSport('ALL'); }}
                  className="mt-4 text-[#111111] font-bold hover:underline"
                >
                  {t.clearFilters}
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </PageShell>
  );
};

export default OrganizationsPage;
